import axios from 'axios';
import fileDownload from 'js-file-download';
import { API_ENDPOINTS, API_REPORTS } from '../config/Config';
import { formatDate } from '@progress/kendo-intl';
import  { ReportTypeEnum } from '../config/Enums';

// The API is on a different origin than the SPA, so the browser's default Referrer-Policy
// (strict-origin-when-cross-origin) strips the path/query from the Referer header it sends -
// the server only ever sees the SPA's origin. Send the real page URL explicitly instead.
axios.interceptors.request.use((config) => {
  config.headers['X-Caller-Url'] = window.location.href;
  return config;
});

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function handler(err: any): Error {
  let error = err;

  if( err.response.data instanceof ArrayBuffer ){
    try {
      error = JSON.parse(new TextDecoder().decode(err.response.data  as ArrayBuffer));
    } catch (e) {
      error = {};
    }
  }

  if (err.response && err.response.data.hasOwnProperty('message')) error = err.response.data;
  else if (error && error.hasOwnProperty('detail')) error.message = error.detail;
  else if (!err.hasOwnProperty('message')) error = err.toJSON();

  return new Error(error.message);
}

export interface IReportParameters {
  type: number;
  status?: number;
  marketIds?: string[];
  sectionIds?: string[];
  ids?: string[];
  startDate?: Date;
  endDate?: Date;
}
const getFileName = (parameters: IReportParameters) => {
  switch (parameters.type) {
    case API_REPORTS.ProductCatalog:
      return `GISPortal_ProductCatalog_${formatDate(new Date(), 'yyyyMMdd')}.xlsx`;
    case API_REPORTS.ProductSecurity:
      return `GISPortal_ProductSecurity_${formatDate(new Date(), 'yyyyMMdd')}.xlsx`;
    default:
      return `GISPortal_ProductTrail_${formatDate(new Date(), 'yyyyMMdd')}.xlsx`;
  }
};
export async function downloadReport(parameters: IReportParameters): Promise<void> {
  try {
    const url = `${API_ENDPOINTS.GET_REPORT}`;
    const res = await axios.post(url, parameters, { headers: { 'Content-Type': 'application/json' }, responseType: 'arraybuffer' });
    fileDownload(res.data, getFileName(parameters));
  } catch (e) {
    throw handler(e);
  }
}
export async function downloadExportReport(entity: string): Promise<void> {
  try {
    const url = `${API_ENDPOINTS.GET_REPORT}`;
    const parameters : IReportParameters = {type: ReportTypeEnum[entity]};
    const res = await axios.post(url, parameters, { headers: { 'Content-Type': 'application/json' }, responseType: 'arraybuffer' });
    fileDownload(res.data, `GISPortal_${entity.toUpperCase()}_${formatDate(new Date(), 'yyyyMMdd')}.xlsx`);
  } catch (e) {
    throw handler(e);
  }
}

/** The GraphQL-style grid variables produced by GridPanel.getFilter(), reused as the REST filter input */
export interface IGridFilterVariables {
  first: number;
  after: string;
  search?: string;
  orderBy?: { field: string; order: string }[];
  entityStatus?: number;
  where?: any[];
  [key: string]: unknown;
}

// The fixed set of variable keys fetchPagedList itself understands - anything else (e.g. a
// `{ reportId }` scoping key from ListViewPanel's `filter` prop or FieldDataBox's `variables`
// prop) is forwarded as its own query param below instead of being silently dropped.
const KNOWN_VARIABLE_KEYS = new Set(['first', 'after', 'search', 'orderBy', 'entityStatus', 'where']);

/** Server-side PaginatedList<T> wire shape (see App.Framework.Domain.ValueObjects.PaginatedList) */
export interface IPagedResult<T> {
  value: T[];
  count: number;
  skip: number;
  take: number;
}

// The REST endpoints bind enums by name, case-insensitively but without underscores, so the
// GraphQL-style constants the grid produces (STARTS_WITH, IN_LIST, ASC...) must be de-snaked
// to match the server names (StartsWith, InList, Asc...).
const toServerEnum = (value?: string | null) => (value ? value.replace(/_/g, '') : value);

const appendFilterParams = (params: URLSearchParams, prefix: string, filter: any) => {
  if (filter.field) params.append(`${prefix}.Field`, filter.field);
  if (filter.operator) params.append(`${prefix}.Operator`, toServerEnum(filter.operator) as string);
  if (filter.logic) params.append(`${prefix}.Logic`, toServerEnum(filter.logic) as string);
  if (filter.value !== undefined && filter.value !== null) params.append(`${prefix}.Value`, `${filter.value}`);
  filter.values?.forEach((v: string) => params.append(`${prefix}.Values`, v));
  if (filter.isNot) params.append(`${prefix}.IsNot`, 'true');
  filter.filters?.forEach((nested: any, i: number) => appendFilterParams(params, `${prefix}.Filters[${i}]`, nested));
};

/**
 * Fetches a page of entities from a REST list endpoint (e.g. /market), translating the grid's
 * GraphQL-style variables into the query-string shape ASP.NET Core binds onto the server-side
 * Filter (Take/Skip/EntityStatus/FilterString/OrderBy[i].../Filters[i]...).
 */
export async function fetchPagedList<T>(url: string, variables: IGridFilterVariables): Promise<IPagedResult<T>> {
  const params = new URLSearchParams();
  params.append('EnablePagination', 'true');
  params.append('Take', `${variables.first ?? 15}`);
  params.append('Skip', `${variables.after ?? 0}`);
  if (variables.entityStatus) params.append('EntityStatus', `${variables.entityStatus}`);
  if (variables.search) params.append('FilterString', variables.search);
  variables.orderBy?.forEach((o, i) => {
    params.append(`OrderBy[${i}].Field`, o.field);
    params.append(`OrderBy[${i}].Order`, toServerEnum(o.order) as string);
  });
  variables.where?.forEach((f, i) => appendFilterParams(params, `Filters[${i}]`, f));
  // Extra scoping keys (e.g. reportId, marketId) bind directly onto the matching property of the
  // server-side Filter (case-insensitive query-string binding), same as Take/Skip/EntityStatus above.
  Object.keys(variables)
    .filter((key) => !KNOWN_VARIABLE_KEYS.has(key) && variables[key] !== undefined && variables[key] !== null && variables[key] !== '')
    .forEach((key) => params.append(key, `${variables[key]}`));

  try {
    const { data } = await axios.get<IPagedResult<T>>(url, { params });
    return data;
  } catch (err: any) {
    // The list endpoints 404 when the filter matches zero records - that's an empty page, not an error.
    if (err?.response?.status === 404) {
      return { value: [], count: 0, skip: Number(variables.after ?? 0), take: variables.first ?? 15 };
    }
    throw handler(err);
  }
}

/** Fetches a single entity by id from a REST endpoint (e.g. GET /market/{id}). */
export async function fetchEntityById<T>(baseUrl: string, id: string): Promise<T> {
  try {
    const { data } = await axios.get<T>(`${baseUrl}/${id}`);
    return data;
  } catch (err: any) {
    throw handler(err);
  }
}

/** Creates an entity via a REST endpoint (e.g. POST /market). */
export async function createEntity<T>(baseUrl: string, payload: any): Promise<T> {
  try {
    const { data } = await axios.post<T>(baseUrl, payload);
    return data;
  } catch (err: any) {
    throw handler(err);
  }
}

/** Updates an entity via a REST endpoint (e.g. PUT /market). */
export async function updateEntity<T>(baseUrl: string, payload: any): Promise<T> {
  try {
    const { data } = await axios.put<T>(baseUrl, payload);
    return data;
  } catch (err: any) {
    throw handler(err);
  }
}

/** Duplicates an entity by id via a REST endpoint (e.g. POST /market/{id}/duplicate). */
export async function duplicateEntity<T>(baseUrl: string, id: string): Promise<T> {
  try {
    const { data } = await axios.post<T>(`${baseUrl}/${id}/duplicate`);
    return data;
  } catch (err: any) {
    throw handler(err);
  }
}

/** Changes an entity's status via a REST endpoint (e.g. PUT /market/{id}/status). */
export async function changeEntityStatus(baseUrl: string, id: string, status: string): Promise<void> {
  try {
    await axios.put(`${baseUrl}/${id}/status`, JSON.stringify(status), { headers: { 'Content-Type': 'application/json' } });
  } catch (err: any) {
    throw handler(err);
  }
}

/** Tests whether a Power BI workspace/dataset is reachable, without saving a connection first (AC #2). */
export async function testConnection(baseUrl: string, workspaceId: string, datasetId: string): Promise<boolean> {
  try {
    const { data } = await axios.get<boolean>(`${baseUrl}/test`, { params: { workspaceId, datasetId } });
    return data;
  } catch (err: any) {
    // The server returns the real reason (e.g. "You don't have access to workspace 'x' / dataset 'y', or it
    // no longer exists.") as a plain string body, not an object - handler() doesn't recognize that shape, so
    // it's read directly here instead, to show the admin the actual cause rather than a generic failure toast.
    const detail = typeof err.response?.data === 'string' ? err.response.data : undefined;
    throw new Error(detail ?? handler(err).message);
  }
}

/** Refreshes a saved connection's dataset metadata from Power BI (AC #5). */
export async function refreshConnectionMetadata<T>(baseUrl: string, id: string): Promise<T> {
  try {
    const { data } = await axios.post<T>(`${baseUrl}/${id}/refresh-metadata`);
    return data;
  } catch (err: any) {
    throw handler(err);
  }
}

export async function getPowerBIGroupList<T>(url: any, params?: Record<string, any>): Promise<T[]> {
    try{
      const { data } = await axios.get<T[]>(url, { params });
      return data;
    }
    catch (error) {
      console.error('Error fetching data:', error);
      throw error;
    }
  }